// src/app/hooks/useSettings.ts
import { useState, useEffect, useCallback } from "react";
import {
  getSettings,
  patchSettings,
  type SettingsData,
  type AccessibilityPreferences,
  type AppearancePreferences,
  type PrivacyPreferences,
  type UserProfile,
} from "@/app/services/common/settings-service";
import { applyUserSettingsToDom } from "../../components/helper/user-settings-apply";

interface UseSettingsResult {
  settings: SettingsData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;

  updateProfile: (patch: Partial<UserProfile>) => Promise<void>;
  updatePrivacy: (patch: Partial<PrivacyPreferences>) => Promise<void>;
  updateAccessibility: (patch: Partial<AccessibilityPreferences>) => Promise<void>;
  updateAppearance: (patch: Partial<AppearancePreferences>) => Promise<void>;
}

export function useSettings(): UseSettingsResult {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getSettings();
        if (cancelled) return;

        setSettings(data);
        setIsLoading(false);
        applyUserSettingsToDom(data);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error("Failed to fetch settings"));
        setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [refetchTrigger]);

  const persist = useCallback(async (next: SettingsData) => {
    // Apply immediately (optimistic)
    setSettings(next);
    applyUserSettingsToDom(next);

    // Persist full section(s) — prevents backend defaults wiping values
    const saved = await patchSettings({
      profile: next.profile,
      privacy: next.privacy,
      accessibility: next.accessibility,
      appearance: next.appearance,
      // keep other fields untouched by not sending them
    } as any);

    setSettings(saved);
    applyUserSettingsToDom(saved);
  }, []);

  const updateProfile = useCallback(async (patch: Partial<UserProfile>) => {
    if (!settings) return;
    const next: SettingsData = { ...settings, profile: { ...settings.profile, ...patch } };
    await persist(next);
  }, [settings, persist]);

  const updatePrivacy = useCallback(async (patch: Partial<PrivacyPreferences>) => {
    if (!settings) return;
    const next: SettingsData = { ...settings, privacy: { ...settings.privacy, ...patch } };
    await persist(next);
  }, [settings, persist]);

  const updateAccessibility = useCallback(async (patch: Partial<AccessibilityPreferences>) => {
    if (!settings) return;
    const next: SettingsData = { ...settings, accessibility: { ...settings.accessibility, ...patch } };
    await persist(next);
  }, [settings, persist]);

  const updateAppearance = useCallback(async (patch: Partial<AppearancePreferences>) => {
    if (!settings) return;
    const next: SettingsData = { ...settings, appearance: { ...settings.appearance, ...patch } };
    await persist(next);
  }, [settings, persist]);

  return {
    settings,
    isLoading,
    error,
    refetch,
    updateProfile,
    updatePrivacy,
    updateAccessibility,
    updateAppearance,
  };
}